'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, AlertCircle, ChevronDown, Search, User, Flag, Clock, CheckCircle2, Circle, ArrowUpRight, FileText, X, TrendingUp, ShieldAlert, Zap, ChevronRight, BarChart2 } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { issueService, auditService, type DbIssue } from '@/lib/supabase/tprmService';
import { useRole } from '@/contexts/RoleContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type Severity = 'Critical' | 'High' | 'Moderate' | 'Low';
type KanbanStatus = 'New' | 'Under Review' | 'Remediation' | 'Pending Validation' | 'Closed';
type CAPStatus = 'Not Started' | 'In Progress' | 'Submitted' | 'Approved' | 'N/A';

interface Issue {
  id: string;
  title: string;
  vendorName: string;
  vendorId: string;
  category: string;
  severity: Severity;
  status: KanbanStatus;
  dateIdentified: string;
  dueDate: string;
  assignedOwner: string;
  department: string;
  capStatus: CAPStatus;
  escalated: boolean;
  daysOpen: number;
  description: string;
}

function dbToIssue(d: DbIssue): Issue {
  return {
    id: d.issue_ref,
    title: d.title || d.description.slice(0, 60),
    vendorName: d.vendor_name,
    vendorId: d.vendor_id || '',
    category: d.category,
    severity: d.severity as Severity,
    status: d.issue_status as KanbanStatus,
    dateIdentified: d.date_identified || '',
    dueDate: d.due_date || '',
    assignedOwner: d.assigned_owner,
    department: d.department,
    capStatus: d.cap_status as CAPStatus,
    escalated: d.escalated,
    daysOpen: d.days_open,
    description: d.description,
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const KANBAN_COLUMNS: { status: KanbanStatus; color: string; icon: React.ElementType }[] = [
  { status: 'New', color: 'blue', icon: Circle },
  { status: 'Under Review', color: 'amber', icon: Clock },
  { status: 'Remediation', color: 'orange', icon: TrendingUp },
  { status: 'Pending Validation', color: 'purple', icon: ShieldAlert },
  { status: 'Closed', color: 'green', icon: CheckCircle2 },
];

const SEVERITY_CONFIG: Record<Severity, { label: string; dot: string; text: string; bg: string }> = {
  Critical: { label: 'Critical', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/40' },
  High: { label: 'High', dot: 'bg-orange-500', text: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/40' },
  Moderate: { label: 'Moderate', dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  Low: { label: 'Low', dot: 'bg-teal-500', text: 'text-teal-700 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/40' },
};

const CAP_CONFIG: Record<CAPStatus, { label: string; color: string }> = {
  'Not Started': { label: 'CAP: Not Started', color: 'text-[hsl(var(--text-muted))] bg-[hsl(var(--surface-raised))]' },
  'In Progress': { label: 'CAP: In Progress', color: 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' },
  'Submitted': { label: 'CAP: Submitted', color: 'text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40' },
  'Approved': { label: 'CAP: Approved', color: 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40' },
  'N/A': { label: 'CAP: N/A', color: 'text-[hsl(var(--text-muted))] bg-[hsl(var(--surface-raised))]' },
};

const COLUMN_HEADER_COLORS: Record<KanbanStatus, string> = {
  'New': 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300',
  'Under Review': 'border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
  'Remediation': 'border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300',
  'Pending Validation': 'border-purple-400 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300',
  'Closed': 'border-teal-400 bg-teal-50 dark:bg-teal-950/30 text-teal-700 dark:text-teal-300',
};

// ─── Issue Card ───────────────────────────────────────────────────────────────

function IssueCard({ issue, onClick }: { issue: Issue; onClick: () => void }) {
  const sev = SEVERITY_CONFIG[issue.severity];
  const cap = CAP_CONFIG[issue.capStatus];
  const isOverdue = issue.dueDate && new Date(issue.dueDate) < new Date() && issue.status !== 'Closed';

  return (
    <div
      onClick={onClick}
      className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-3 cursor-pointer hover:shadow-md hover:border-[hsl(var(--border-strong))] transition-all duration-150 group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full ${sev.bg} ${sev.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
            {issue.severity}
          </span>
          {issue.escalated && (
            <span className="inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400">
              <Zap size={9} />
              Escalated
            </span>
          )}
        </div>
        <span className="text-2xs text-[hsl(var(--text-muted))] font-mono flex-shrink-0">{issue.id}</span>
      </div>

      <p className="text-xs font-semibold text-[hsl(var(--text-primary))] leading-snug mb-1.5 group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors">
        {issue.title}
      </p>
      <p className="text-2xs text-[hsl(var(--text-muted))] mb-2 truncate">{issue.vendorName}</p>

      <div className="mb-2">
        <span className="text-2xs px-1.5 py-0.5 rounded bg-[hsl(var(--surface-raised))] text-[hsl(var(--text-secondary))] border border-[hsl(var(--border))]">
          {issue.category}
        </span>
      </div>

      <div className="mb-2">
        <span className={`inline-flex items-center gap-1 text-2xs font-medium px-1.5 py-0.5 rounded ${cap.color}`}>
          <FileText size={9} />
          {cap.label}
        </span>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-[hsl(var(--border))]">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center text-white flex-shrink-0" style={{ fontSize: '7px', fontWeight: 700 }}>
            {issue.assignedOwner.split(' ').map((n) => n[0]).join('').slice(0, 2)}
          </div>
          <span className="text-2xs text-[hsl(var(--text-muted))] truncate max-w-[80px]">{issue.assignedOwner}</span>
        </div>
        <div className={`flex items-center gap-1 text-2xs ${isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-[hsl(var(--text-muted))]'}`}>
          {isOverdue && <AlertCircle size={10} />}
          <Clock size={9} />
          {issue.dueDate}
        </div>
      </div>
    </div>
  );
}

// ─── Issue Detail Drawer ──────────────────────────────────────────────────────

function IssueDetailDrawer({ issue, onClose, onStatusAdvance }: { issue: Issue; onClose: () => void; onStatusAdvance: (id: string, newStatus: KanbanStatus) => void }) {
  const sev = SEVERITY_CONFIG[issue.severity];
  const cap = CAP_CONFIG[issue.capStatus];
  const isOverdue = issue.dueDate && new Date(issue.dueDate) < new Date() && issue.status !== 'Closed';
  const { can } = useRole();

  const STATUS_ORDER: KanbanStatus[] = ['New', 'Under Review', 'Remediation', 'Pending Validation', 'Closed'];
  const currentIdx = STATUS_ORDER.indexOf(issue.status);
  const nextStatus = STATUS_ORDER[currentIdx + 1] as KanbanStatus | undefined;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[hsl(var(--surface))] border-l border-[hsl(var(--border))] h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-[hsl(var(--border))] sticky top-0 bg-[hsl(var(--surface))] z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-[hsl(var(--text-muted))]">{issue.id}</span>
              {issue.escalated && (
                <span className="inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400">
                  <Zap size={9} /> Escalated
                </span>
              )}
            </div>
            <h2 className="text-sm font-bold text-[hsl(var(--text-primary))] leading-snug">{issue.title}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 ml-2 flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Status</p>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${COLUMN_HEADER_COLORS[issue.status]}`}>
                {issue.status}
              </span>
            </div>
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Severity</p>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${sev.bg} ${sev.text}`}>
                <span className={`w-2 h-2 rounded-full ${sev.dot}`} />
                {issue.severity}
              </span>
            </div>
          </div>

          <div>
            <p className="text-2xs text-[hsl(var(--text-muted))] mb-1.5 font-medium uppercase tracking-wide">Description</p>
            <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed">{issue.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Vendor</p>
              <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{issue.vendorName}</p>
              <p className="text-2xs text-[hsl(var(--text-muted))]">{issue.vendorId}</p>
            </div>
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Category</p>
              <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{issue.category}</p>
            </div>
          </div>

          <div>
            <p className="text-2xs text-[hsl(var(--text-muted))] mb-1.5 font-medium uppercase tracking-wide">Corrective Action Plan</p>
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg ${cap.color}`}>
              <FileText size={12} />
              {cap.label}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Assigned Owner</p>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center text-white text-2xs font-bold flex-shrink-0">
                  {issue.assignedOwner.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{issue.assignedOwner}</p>
              </div>
            </div>
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Department</p>
              <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{issue.department}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Date Identified</p>
              <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{issue.dateIdentified}</p>
            </div>
            <div>
              <p className="text-2xs text-[hsl(var(--text-muted))] mb-1 font-medium uppercase tracking-wide">Due Date</p>
              <p className={`text-xs font-medium ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-[hsl(var(--text-primary))]'}`}>
                {issue.dueDate}
                {isOverdue && <span className="ml-1 text-2xs">(Overdue)</span>}
              </p>
            </div>
          </div>

          <div className="bg-[hsl(var(--surface-raised))] rounded-lg p-3 flex items-center justify-between">
            <span className="text-xs text-[hsl(var(--text-secondary))]">Days Open</span>
            <span className={`text-sm font-bold ${issue.daysOpen > 60 ? 'text-red-600 dark:text-red-400' : issue.daysOpen > 30 ? 'text-amber-600 dark:text-amber-400' : 'text-[hsl(var(--text-primary))]'}`}>
              {issue.daysOpen}d
            </span>
          </div>
        </div>

        <div className="p-5 border-t border-[hsl(var(--border))] space-y-2">
          {nextStatus && can('issue:advance') && (
            <button
              onClick={() => { onStatusAdvance(issue.id, nextStatus); onClose(); }}
              className="btn-primary w-full justify-center text-xs"
            >
              <ArrowUpRight size={14} /> Advance to {nextStatus}
            </button>
          )}
          <div className="grid grid-cols-2 gap-2">
            {can('issue:reassign') && (
            <button className="btn-secondary text-xs justify-center">
              <User size={12} /> Reassign
            </button>
            )}
            {can('issue:escalate') && (
            <button className="btn-secondary text-xs justify-center">
              <Flag size={12} /> Escalate
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Board ───────────────────────────────────────────────────────────────

export default function IssuesBoard() {
  const searchParams = useSearchParams();
  const [dbIssues, setDbIssues] = useState<DbIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('All');
  const [severityFilter, setSeverityFilter] = useState(searchParams.get('severity') || 'All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'All');
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const { can } = useRole();

  // Detect if we arrived from Analytics (has severity or status param)
  const fromAnalytics = !!(searchParams.get('severity') || searchParams.get('status'));
  const analyticsSeverity = searchParams.get('severity') || '';
  const analyticsStatus = searchParams.get('status') || '';

  // Load issues from Supabase with real-time subscription
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    issueService.getAll().then((data) => {
      if (!cancelled) {
        setDbIssues(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    const unsubscribe = issueService.subscribeToAll((updated) => {
      if (!cancelled) setDbIssues(updated);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const issues = useMemo(() => dbIssues.map(dbToIssue), [dbIssues]);

  const UNIQUE_VENDORS = useMemo(() => Array.from(new Set(issues.map((i) => i.vendorName))).sort(), [issues]);
  const UNIQUE_CATEGORIES = useMemo(() => Array.from(new Set(issues.map((i) => i.category))).sort(), [issues]);

  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      const matchesSearch =
        !searchQuery ||
        issue.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.vendorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesVendor = vendorFilter === 'All' || issue.vendorName === vendorFilter;
      const matchesSeverity = severityFilter === 'All' || issue.severity === severityFilter;
      const matchesCategory = categoryFilter === 'All' || issue.category === categoryFilter;
      const matchesStatus = statusFilter === 'All' || issue.status === statusFilter;
      return matchesSearch && matchesVendor && matchesSeverity && matchesCategory && matchesStatus;
    });
  }, [issues, searchQuery, vendorFilter, severityFilter, categoryFilter, statusFilter]);

  const issuesByStatus = useMemo(() => {
    const map: Record<KanbanStatus, Issue[]> = {
      'New': [], 'Under Review': [], 'Remediation': [], 'Pending Validation': [], 'Closed': [],
    };
    filteredIssues.forEach((issue) => {
      if (map[issue.status]) map[issue.status].push(issue);
    });
    return map;
  }, [filteredIssues]);

  const totalOpen = issues.filter((i) => i.status !== 'Closed').length;
  const criticalCount = issues.filter((i) => i.severity === 'Critical' && i.status !== 'Closed').length;
  const escalatedCount = issues.filter((i) => i.escalated && i.status !== 'Closed').length;
  const overdueCount = issues.filter((i) => i.dueDate && new Date(i.dueDate) < new Date() && i.status !== 'Closed').length;

  async function handleStatusAdvance(issueRef: string, newStatus: KanbanStatus) {
    const dbIssue = dbIssues.find((d) => d.issue_ref === issueRef);
    if (!dbIssue) return;

    // Optimistic update
    setDbIssues((prev) => prev.map((d) => d.issue_ref === issueRef ? { ...d, issue_status: newStatus } : d));

    try {
      await issueService.updateStatus(dbIssue.id, newStatus);
      await auditService.log({
        entity_type: 'Issue',
        entity_id: issueRef,
        action: 'Status Advanced',
        actor: 'Current User',
        actor_role: 'Procurement',
        detail: `Issue ${issueRef} advanced to ${newStatus}.`,
      });
    } catch {
      // Revert on failure
      setDbIssues((prev) => prev.map((d) => d.issue_ref === issueRef ? { ...d, issue_status: dbIssue.issue_status } : d));
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full min-h-0 items-center justify-center gap-3">
        <div className="w-6 h-6 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-[hsl(var(--text-muted))]">Loading issues…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Breadcrumb — shown when arriving from Analytics */}
      {fromAnalytics && (
        <div className="px-6 py-2 bg-[hsl(var(--surface-raised))] border-b border-[hsl(var(--border))] flex items-center gap-1.5 text-xs flex-shrink-0">
          <BarChart2 size={12} className="text-[hsl(var(--accent))]" />
          <Link href="/analytics" className="text-[hsl(var(--accent))] hover:underline font-medium">
            Analytics
          </Link>
          <ChevronRight size={11} className="text-[hsl(var(--text-muted))]" />
          <span className="text-[hsl(var(--text-muted))]">Issues</span>
          {(analyticsSeverity || analyticsStatus) && (
            <>
              <ChevronRight size={11} className="text-[hsl(var(--text-muted))]" />
              <span className="text-[hsl(var(--text-primary))] font-medium">
                {[analyticsSeverity && `Severity: ${analyticsSeverity}`, analyticsStatus && `Status: ${analyticsStatus}`].filter(Boolean).join(' · ')}
              </span>
            </>
          )}
          <button
            onClick={() => { setVendorFilter('All'); setSeverityFilter('All'); setCategoryFilter('All'); setStatusFilter('All'); setSearchQuery(''); }}
            className="ml-auto flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 px-2 py-0.5 rounded-md transition-colors"
          >
            <X size={11} /> Clear Filters
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">Issues</h1>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">Track and remediate vendor compliance, security, and performance issues</p>
          </div>
          {can('issue:create') && (
          <button className="btn-primary text-xs">
            <AlertTriangle size={13} /> New Issue
          </button>
          )}
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Open Issues', value: totalOpen, color: 'text-[hsl(var(--text-primary))]', bg: 'bg-[hsl(var(--surface-raised))]' },
            { label: 'Critical', value: criticalCount, color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30' },
            { label: 'Escalated', value: escalatedCount, color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30' },
            { label: 'Overdue', value: overdueCount, color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30' },
          ].map((kpi) => (
            <div key={kpi.label} className={`${kpi.bg} rounded-lg px-3 py-2.5 border border-[hsl(var(--border))]`}>
              <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-7 text-xs h-8 w-full"
            />
          </div>

          <div className="relative">
            <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} className="input-field text-xs h-8 pr-7 appearance-none cursor-pointer min-w-[140px]">
              <option value="All">All Vendors</option>
              {UNIQUE_VENDORS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))] pointer-events-none" />
          </div>

          <div className="relative">
            <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="input-field text-xs h-8 pr-7 appearance-none cursor-pointer min-w-[120px]">
              <option value="All">All Severities</option>
              {(['Critical', 'High', 'Moderate', 'Low'] as Severity[]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))] pointer-events-none" />
          </div>

          <div className="relative">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field text-xs h-8 pr-7 appearance-none cursor-pointer min-w-[140px]">
              <option value="All">All Statuses</option>
              {(['New', 'Under Review', 'Remediation', 'Pending Validation', 'Closed'] as KanbanStatus[]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))] pointer-events-none" />
          </div>

          <div className="relative">
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="input-field text-xs h-8 pr-7 appearance-none cursor-pointer min-w-[130px]">
              <option value="All">All Categories</option>
              {UNIQUE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))] pointer-events-none" />
          </div>

          {(vendorFilter !== 'All' || severityFilter !== 'All' || categoryFilter !== 'All' || statusFilter !== 'All' || searchQuery) && (
            <button
              onClick={() => { setVendorFilter('All'); setSeverityFilter('All'); setCategoryFilter('All'); setStatusFilter('All'); setSearchQuery(''); }}
              className="btn-ghost text-xs h-8 text-[hsl(var(--text-muted))] hover:text-red-600"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-3 h-full p-4 min-w-max">
          {KANBAN_COLUMNS.map(({ status, icon: ColIcon }) => {
            const columnIssues = issuesByStatus[status];
            const headerClass = COLUMN_HEADER_COLORS[status];
            return (
              <div key={status} className="flex flex-col w-64 flex-shrink-0 h-full">
                <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-t-2 ${headerClass} mb-0`}>
                  <div className="flex items-center gap-1.5">
                    <ColIcon size={13} />
                    <span className="text-xs font-semibold">{status}</span>
                  </div>
                  <span className="text-xs font-bold tabular-nums bg-white/50 dark:bg-black/20 px-1.5 py-0.5 rounded-full">
                    {columnIssues.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto bg-[hsl(var(--surface-raised))] border border-[hsl(var(--border))] border-t-0 rounded-b-lg p-2 space-y-2">
                  {columnIssues.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <CheckCircle2 size={20} className="text-[hsl(var(--text-muted))] mb-2 opacity-40" />
                      <p className="text-2xs text-[hsl(var(--text-muted))]">No issues</p>
                    </div>
                  ) : (
                    columnIssues.map((issue) => (
                      <IssueCard key={issue.id} issue={issue} onClick={() => setSelectedIssue(issue)} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedIssue && (
        <IssueDetailDrawer
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onStatusAdvance={handleStatusAdvance}
        />
      )}
    </div>
  );
}
